import { useAuth } from '@iris/ui/auth/auth-provider';
import { BrandSplash } from '@iris/ui/components/brand-mark';
import { safeReturnTo } from '@iris/ui/lib/auth-helpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';

/**
 * Screens that need a session (T4 §1). No session → /login with the return URL (none after a chosen
 * sign-out). Offline at boot → cached screens stay readable.
 */
export function RequireAuth() {
  const { status, signedOut, clearNextAfterSignIn } = useAuth();
  const location = useLocation();
  const here = location.pathname + location.search;
  const showing = status === 'authenticated' || status === 'offline';
  useEffect(() => {
    if (showing) clearNextAfterSignIn();
  }, [showing, clearNextAfterSignIn]);

  if (status === 'loading') return <Splash />;
  if (status === 'anonymous') {
    return <Navigate to={signedOut ? '/login' : `/login?returnTo=${encodeURIComponent(here)}`} replace />;
  }
  return <Outlet />;
}

/** Login, sign-up and forgot-password: someone already logged in goes on to the return URL (or home). */
export function PublicOnly({ home = '/' }: { home?: string }) {
  const { status, nextAfterSignIn } = useAuth();
  const [params] = useSearchParams();

  if (status === 'loading') return <Splash />;
  if (status === 'authenticated') {
    return <Navigate to={nextAfterSignIn ?? safeReturnTo(params.get('returnTo'), home)} replace />;
  }
  return <Outlet />;
}

/** ADR 0016: while the session is checked at start-up, the Bonustiva splash (continues the HTML one). */
function Splash() {
  const { t } = useTranslation();
  return <BrandSplash name={t('brand.name')} slogan={t('brand.slogan')} label={t('common.loading')} />;
}
